import React from 'react';

import { Box, Col, type ColProps, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Loader } from '@opendoor/atoms/loader';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { IconShare } from '@tabler/icons-react';
import { FavoriteHeartIcon } from 'components/marketplace/shared/FavoriteHeartIcon';
import { PropertyPhotoImage } from 'components/marketplace/shared/PropertyPhoto';

import { formatCurrencyDollars } from '../../helpers/formatters';
import { Pill } from './shared/Pill';

type LinkColProps = Extract<ColProps, { href: string }>;

type CardLinkProps = Pick<LinkColProps, 'children' | 'className' | 'external'> & {
  ariaLabel: string;
  analyticsName: string;
  href?: string | null;
};

export const CardLink: React.FC<CardLinkProps> = ({
  href,
  ariaLabel,
  analyticsName,
  className,
  children,
  external,
}) => {
  if (href == null) {
    return <Col className={className}>{children}</Col>;
  }

  return (
    <Col
      href={href}
      external={external}
      aria-label={ariaLabel}
      analyticsName={analyticsName}
      className={className}
    >
      {children}
    </Col>
  );
};

export type PropertyCardProps = {
  contained?: boolean;
  /** Optional details replace the default price, facts, and address block. */
  details?: React.ReactNode;
  actionsOnPhoto?: boolean;
  beds?: number | null;
  baths?: number | null;
  city?: string | null;
  dwellingType?: string | null;
  href?: string | null;
  openLinksInNewTab?: boolean;
  isFavorite?: boolean | null;
  isFavoriteLoading?: boolean;
  neighborhood?: string | null;
  onFavoritePress?: () => void;
  onSharePress?: () => void;
  isPhotoPending?: boolean;
  photoMedia?: React.ReactElement;
  photoUrl?: string | null;
  /**
   * Rendered inside the photo container, on top of the photo — badges, hover
   * treatments, etc. Position children with `absolute`; the container is
   * `relative` and clips to the photo's rounded bounds, so consumers never
   * have to mirror the card's aspect ratio or radius.
   */
  photoOverlay?: React.ReactNode;
  photoFetchPriority?: React.ImgHTMLAttributes<HTMLImageElement>['fetchPriority'];
  price?: number | null;
  /** Shows a "Self-tour available" badge next to the price. */
  selfTourAvailable?: boolean | null;
  /** Customer-facing availability label, such as "Available soon". */
  statusLabel?: string | null;
  sqft?: number | null;
};

const PROPERTY_CARD_IMAGE_CLASS = 'aspect-[16/9] w-full object-cover';

function cardPriceLabel(price?: number | null, statusLabel?: string | null) {
  if (price != null) {
    return formatCurrencyDollars(price);
  }
  return statusLabel ? 'Price pending' : null;
}

type PropertyCardActionsProps = Pick<
  PropertyCardProps,
  'isFavorite' | 'isFavoriteLoading' | 'onFavoritePress' | 'onSharePress' | 'actionsOnPhoto'
>;

// Inset the visible circle while retaining the full pointer/touch target.
const PHOTO_ACTION_CLASS_NAME =
  'relative isolate size-11 bg-transparent shadow-none before:absolute before:inset-1 before:-z-1 before:rounded-full before:bg-od-bg-primary/90 before:shadow-sm not-disabled:hover:bg-transparent not-disabled:hover:before:bg-od-bg-hover-primary not-disabled:active:bg-transparent sm:size-10';

const PropertyCardActions = ({
  isFavorite,
  isFavoriteLoading,
  onFavoritePress,
  onSharePress,
  actionsOnPhoto = false,
}: PropertyCardActionsProps) => {
  if (!onFavoritePress && !onSharePress) {
    return null;
  }

  const hasShareAction = onSharePress != null;
  const defaultIconSize = hasShareAction ? 22 : 20;
  const iconSize = actionsOnPhoto ? 18 : defaultIconSize;

  return (
    <Row className={cn(hasShareAction ? 'shrink-0 items-center gap-1' : 'justify-end self-center')}>
      {onSharePress ? (
        <Button
          variant="secondary"
          aria-label="Share home"
          analyticsName="marketplace-property-card-share-button"
          onClick={onSharePress}
          className={cn(
            'size-10 rounded-full border-0 bg-transparent p-0 not-disabled:hover:bg-od-bg-hover-primary',
            actionsOnPhoto && PHOTO_ACTION_CLASS_NAME,
          )}
        >
          <IconShare size={iconSize} />
        </Button>
      ) : null}
      {onFavoritePress ? (
        <Button
          variant="secondary"
          aria-label={isFavorite ? 'unfavorite' : 'favorite'}
          analyticsName="marketplace-property-card-favorite-button"
          onClick={onFavoritePress}
          // The Button's `loading` dots are sized for a text label and overflow
          // this icon-only circle. `disabled` with `aria-busy` keeps the same
          // busy behavior — blocked input, un-greyed colors — around a spinner
          // that fits the circle.
          disabled={isFavoriteLoading}
          aria-busy={isFavoriteLoading || undefined}
          className={cn(
            'group/favorite rounded-full border-0 bg-transparent p-0',
            hasShareAction ? 'size-10 not-disabled:hover:bg-od-bg-hover-primary' : 'size-6',
            actionsOnPhoto && PHOTO_ACTION_CLASS_NAME,
          )}
        >
          {isFavoriteLoading ? (
            <Loader aria-label="Loading favorite" size="sm" />
          ) : (
            <FavoriteHeartIcon filled={isFavorite === true} interactive size={iconSize} />
          )}
        </Button>
      ) : null}
    </Row>
  );
};

export const PropertyCard: React.FC<PropertyCardProps> = ({
  contained = false,
  details,
  actionsOnPhoto = false,
  beds,
  baths,
  city,
  dwellingType,
  href,
  openLinksInNewTab = false,
  isFavorite,
  isFavoriteLoading,
  neighborhood,
  onFavoritePress,
  onSharePress,
  isPhotoPending,
  photoMedia,
  photoUrl,
  photoFetchPriority,
  photoOverlay,
  price,
  selfTourAvailable,
  statusLabel,
  sqft,
}) => {
  const hasShareAction = onSharePress != null;

  return (
    <Col
      className={cn(
        'w-full',
        contained && 'h-full overflow-hidden rounded-2xl bg-od-bg-primary',
        !contained && (hasShareAction ? 'gap-3' : 'gap-5'),
      )}
    >
      <Box className="relative w-full">
        {photoMedia ? (
          <Col
            className={cn(
              'relative aspect-[16/9] w-full overflow-hidden bg-od-bg-secondary',
              !contained && 'rounded-2xl',
            )}
          >
            {photoMedia}
            {statusLabel && !photoOverlay ? (
              <Col className="absolute left-3 top-3">
                <Pill label={statusLabel} rounded size="small" />
              </Col>
            ) : null}
            {photoOverlay}
          </Col>
        ) : (
          <CardLink
            href={href}
            external={openLinksInNewTab}
            ariaLabel="Main view of property"
            analyticsName="marketplace-property-card-image-link"
            className={cn('relative w-full overflow-hidden', !contained && 'rounded-2xl')}
          >
            <PropertyPhotoImage
              alt="Main view of property"
              loading="lazy"
              containerClassName={PROPERTY_CARD_IMAGE_CLASS}
              className="h-full w-full object-cover"
              placeholderClassName="bg-od-bg-tertiary"
              placeholderLabel={statusLabel ? 'Photos coming soon' : undefined}
              isPending={isPhotoPending}
              src={photoUrl}
              fetchPriority={photoFetchPriority}
            />
            {statusLabel && !photoOverlay ? (
              <Col className="absolute left-3 top-3">
                <Pill label={statusLabel} rounded size="small" />
              </Col>
            ) : null}
            {photoOverlay}
          </CardLink>
        )}
        {actionsOnPhoto && (
          <Box className="absolute right-2 top-2 z-2">
            <PropertyCardActions
              actionsOnPhoto
              isFavorite={isFavorite}
              isFavoriteLoading={isFavoriteLoading}
              onFavoritePress={onFavoritePress}
              onSharePress={onSharePress}
            />
          </Box>
        )}
      </Box>
      <Col className={cn(hasShareAction ? 'min-w-0 gap-1.5' : 'gap-3', contained && 'px-3 py-4')}>
        {details ?? (
          <>
            <Row className={cn('items-center justify-between', hasShareAction && 'min-h-10 gap-2')}>
              <Row className={cn('items-center gap-2', hasShareAction && 'min-w-0')}>
                {(price != null || statusLabel) && (
                  <Text as="span" className="text-subheader-sm text-od-content-primary">
                    {cardPriceLabel(price, statusLabel)}
                  </Text>
                )}
                {selfTourAvailable && (
                  <Box className="rounded-full bg-od-bg-accent-brand px-2 py-1">
                    <Text as="span" className="text-label-xs text-od-content-inverse-primary">
                      Self-tour
                    </Text>
                  </Box>
                )}
              </Row>
              {!actionsOnPhoto && (
                <PropertyCardActions
                  isFavorite={isFavorite}
                  isFavoriteLoading={isFavoriteLoading}
                  onFavoritePress={onFavoritePress}
                  onSharePress={onSharePress}
                />
              )}
            </Row>
            <CardLink
              href={href}
              external={openLinksInNewTab}
              ariaLabel="Property details"
              analyticsName="marketplace-property-card-info-link"
              className="w-full"
            >
              <Text as="span" className="text-body-sm text-od-content-tertiary">
                {[
                  beds != null ? `${beds} bds` : null,
                  baths != null ? `${baths} ba` : null,
                  sqft != null ? `${sqft.toLocaleString('en-US')} sqft` : null,
                  dwellingType,
                ]
                  .filter(Boolean)
                  .join(' • ')}
              </Text>
              <Text as="span" className="text-body-sm text-od-content-tertiary">
                {[neighborhood, city].filter(Boolean).join(', ')}
              </Text>
            </CardLink>
          </>
        )}
      </Col>
    </Col>
  );
};
